"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowUpRight, CreditCard, RefreshCw } from "lucide-react";
import { useSession } from "@/components/session-provider";
import { Busy, ErrorNotice, IconButton, PageHeading, Status } from "@/components/ui";
import { errorMessage, mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";
import type { Tenant } from "@/lib/types";

interface Plan { slug: string; name: string; priceCents: number; interval: string; currency: string }
interface Billing { tenant: Tenant | null; plans: Plan[]; baseDomain: string; checkoutEnabled: boolean; canPurchase: boolean }

export function BillingPage() {
  const session = useSession();
  const router = useRouter();
  const resource = useResource<Billing>("/billing", 15000);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const data = resource.data;
  const tenant = data?.tenant;
  async function open(path: string, body: unknown) {
    setPending(true); setError("");
    try {
      const result = await mutation<{ url: string }>(path, body);
      const url = new URL(result.url);
      if (url.protocol !== "https:" || !["checkout.stripe.com", "billing.stripe.com"].includes(url.hostname)) throw new Error("Invalid billing redirect.");
      window.location.assign(url.href);
    } catch (error) { setError(errorMessage(error)); setPending(false); }
  }
  async function changePlan(plan: Plan) {
    const current = data?.plans.find((p) => p.slug === tenant?.planSlug);
    const downgrade = current && plan.priceCents < current.priceCents;
    const note = downgrade && tenant?.customDomainPurchased ? " Your custom domain will stop working on this plan." : "";
    if (!window.confirm(`Switch to ${plan.name} for $${(plan.priceCents / 100).toFixed(2)}/month? The prorated difference is invoiced now.${note}`)) return;
    setPending(true); setError("");
    try { await mutation("/billing/plan", { planSlug: plan.slug }); resource.refresh(); void session.refresh(); }
    catch (error) { setError(errorMessage(error)); }
    finally { setPending(false); }
  }
  return <>
    <PageHeading title="Billing">
      <IconButton label="Refresh billing" onClick={() => { resource.refresh(); void session.refresh(); }}><RefreshCw size={16} /></IconButton>
      {tenant?.planSlug && data?.canPurchase && <button className="button" disabled={pending || !data.checkoutEnabled} onClick={() => void open("/billing/portal", {})}><CreditCard size={16} />Manage subscription</button>}
    </PageHeading>
    <ErrorNotice message={error || resource.error} retry={resource.refresh} />
    {resource.loading ? <Busy /> : data && <>
      {tenant ? <section className="settings-section">
        <h2>{tenant.name || tenant.slug}</h2>
        <dl className="server-details">
          <div><dt>Status</dt><dd><Status value={tenant.state} /></dd></div>
          <div><dt>Plan</dt><dd>{data.plans.find((plan) => plan.slug === tenant.planSlug)?.name || "Not subscribed"}</dd></div>
          <div><dt>Subdomain</dt><dd className="mono">{tenant.slug}.{data.baseDomain}</dd></div>
          <div><dt>Access Until</dt><dd>{tenant.accessUntil ? new Date(tenant.accessUntil).toLocaleString() : "--"}</dd></div>
          <div><dt>Velnqo Nodes</dt><dd>Customer supplied</dd></div>
          <div><dt>Server limit</dt><dd>Unlimited</dd></div>
        </dl>
      </section> : <p className="muted">Platform staff account. No customer subscription.</p>}
      {!data.checkoutEnabled && <div className="error-notice" role="status"><CreditCard size={17} /><span>Purchases are not configured yet.</span></div>}
      {tenant && <div className="table-scroll"><table className="data-table">
        <thead><tr><th>Plan</th><th>Price</th><th>Domain</th><th /></tr></thead>
        <tbody>{data.plans.map((plan) => {
          const addon = plan.interval === "one_time";
          const subscribed = !!tenant.planSlug && tenant.state !== "canceled";
          const current = data.plans.find((p) => p.slug === tenant.planSlug);
          const switchable = !addon && subscribed && plan.slug !== tenant.planSlug;
          const eligible = addon ? tenant.state === "active" && tenant.planSlug === "company" && !tenant.customDomainPurchased : !subscribed || switchable;
          const label = addon && tenant.customDomainPurchased ? "Purchased" : plan.slug === tenant.planSlug ? "Current plan" : addon ? "Add domain" : switchable ? (current && plan.priceCents < current.priceCents ? "Downgrade" : "Upgrade") : "Subscribe";
          return <tr key={plan.slug}><td><strong>{plan.name}</strong></td><td>${(plan.priceCents / 100).toFixed(2)} {addon ? "one-time" : "/ month"}</td><td>{addon ? "Custom domain" : `*.${data.baseDomain}`}</td><td><button className="button" disabled={pending || !data.checkoutEnabled || !data.canPurchase || !eligible} onClick={() => void (switchable ? changePlan(plan) : router.push(`/billing/checkout?plan=${encodeURIComponent(plan.slug)}`))}><ArrowUpRight size={15} />{label}</button></td></tr>;
        })}</tbody>
      </table></div>}
    </>}
  </>;
}
